import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { ApprovalCard } from '../components/approval';
import { MessageList } from '../components/messages';
import { api, type SessionState, type StoredMessage } from '../lib/api';
import { errorMessage, short, usd, when } from '../lib/format';

export function ChatPage() {
  const params = useParams({ strict: false }) as { threadId?: string };
  const threadId = params.threadId;
  const threads = useQuery({
    queryKey: ['threads'],
    queryFn: api.threads,
    refetchInterval: 15_000,
  });
  return (
    <div className="chat">
      <aside className="threads stack">
        <Link to="/chat" className="primary" style={{ textAlign: 'center' }}>
          + New thread
        </Link>
        {threads.data?.threads.map((t) => (
          <Link
            key={t.id}
            to="/chat/$threadId"
            params={{ threadId: t.id }}
            className={t.id === threadId ? 'active' : ''}
          >
            <div>{short(t.title ?? 'untitled', 40)}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {when(t.lastActivityAt)}
            </div>
          </Link>
        ))}
      </aside>
      {threadId ? <Conversation key={threadId} threadId={threadId} /> : <NewThread />}
    </div>
  );
}

function NewThread() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const create = useMutation({
    mutationFn: () => api.createThread(text.trim()),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['threads'] });
      await nav({ to: '/chat/$threadId', params: { threadId: r.id } });
    },
  });
  return (
    <div className="conversation">
      <div className="conv-head">
        <span className="muted">New thread</span>
      </div>
      <div className="messages">
        <div className="panel" style={{ maxWidth: 640 }}>
          <h2 style={{ marginTop: 0 }}>What do you want to work on?</h2>
          <p className="muted">
            The agent has a shell and files in your workspace VM, web fetch, and the read-only tools
            of your connectors. Anything that places or changes an order comes back to you as an
            Execute button. Try: <i>what is my account balance on haipa?</i> or{' '}
            <i>backtest a 20/50 SMA cross on ETH 1h for the last 90 days</i>.
          </p>
        </div>
      </div>
      <Composer
        value={text}
        onChange={setText}
        busy={create.isPending}
        error={create.error}
        onSubmit={() => create.mutate()}
      />
    </div>
  );
}

/** Thread view: initial load over REST, then SSE for new messages + session state (1 s server poll). */
export function Conversation({ threadId }: { threadId: string }) {
  const qc = useQueryClient();
  const initial = useQuery({ queryKey: ['thread', threadId], queryFn: () => api.thread(threadId) });
  const [messages, setMessages] = useState<StoredMessage[]>([]);
  const [session, setSession] = useState<SessionState | null>(null);
  const [text, setText] = useState('');
  const [reconnectKey, setReconnectKey] = useState(0);
  const bottom = useRef<HTMLDivElement>(null);
  const lastSeq = useRef(-1);

  useEffect(() => {
    if (!initial.data) return;
    setMessages(initial.data.messages);
    setSession(initial.data.session);
    lastSeq.current = initial.data.messages.at(-1)?.seq ?? -1;
  }, [initial.data]);

  // SSE: closes itself when the session ends; a new message re-opens it (reconnectKey).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reconnectKey is the intentional re-open trigger
  useEffect(() => {
    if (!initial.data) return;
    const es = new EventSource(`/v1/threads/${threadId}/events?after=${lastSeq.current}`);
    es.addEventListener('message', (e) => {
      const m = JSON.parse((e as MessageEvent).data) as StoredMessage;
      lastSeq.current = Math.max(lastSeq.current, m.seq);
      setMessages((prev) => (prev.some((x) => x.seq === m.seq) ? prev : [...prev, m]));
    });
    es.addEventListener('session', (e) => {
      const s = JSON.parse((e as MessageEvent).data) as SessionState | null;
      setSession(s);
      if (s?.status === 'ended') es.close();
      qc.invalidateQueries({ queryKey: ['credits'] });
    });
    es.onerror = () => {
      // The server ends the stream when the session is over; EventSource would retry forever.
      if (es.readyState === EventSource.CLOSED) return;
    };
    return () => es.close();
  }, [threadId, initial.data, reconnectKey, qc]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when the transcript grows or state changes
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, session?.status]);

  const send = useMutation({
    mutationFn: () => api.sendMessage(threadId, text.trim()),
    onSuccess: () => {
      setText('');
      setReconnectKey((k) => k + 1);
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
  const cancel = useMutation({ mutationFn: () => api.cancelThread(threadId) });

  const busy =
    session?.status === 'thinking' ||
    session?.status === 'running_tools' ||
    session?.status === 'awaiting_approval';
  const title = initial.data?.thread.title ?? 'thread';
  return (
    <div className="conversation">
      <div className="conv-head">
        <span>{short(title, 60)}</span>
        <StatusPill session={session} />
        <span className="grow" />
        {session && <span className="muted">spent {usd(session.totalCostMicroUsd)}</span>}
        {busy && (
          <button type="button" className="small" onClick={() => cancel.mutate()}>
            Stop
          </button>
        )}
      </div>
      <div className="messages">
        {initial.isLoading && <span className="muted">Loading…</span>}
        {initial.error && <span className="error">{errorMessage(initial.error)}</span>}
        <MessageList threadId={threadId} messages={messages} />
        {session?.status === 'awaiting_approval' &&
          session.pending?.map((p) => <ApprovalCard key={p.callId} threadId={threadId} item={p} />)}
        {session?.lastError && <div className="error">Last error: {session.lastError}</div>}
        {session?.endedReason === 'insufficient_credit' && (
          <div className="error">
            Out of credits. <Link to="/account">Top up</Link> and send a message to resume.
          </div>
        )}
        <div ref={bottom} />
      </div>
      <Composer
        value={text}
        onChange={setText}
        busy={send.isPending}
        error={send.error}
        onSubmit={() => send.mutate()}
        placeholder={busy ? 'Agent is working… your message will be queued' : 'Message the agent'}
      />
    </div>
  );
}

function StatusPill({ session }: { session: SessionState | null }) {
  if (!session) return <span className="pill">no session</span>;
  switch (session.status) {
    case 'thinking':
      return <span className="pill busy">thinking · step {session.step}</span>;
    case 'running_tools':
      return <span className="pill busy">running tools · step {session.step}</span>;
    case 'awaiting_approval':
      return <span className="pill danger">waiting for your approval</span>;
    case 'idle':
      return (
        <span className="pill live">idle{session.queued ? ` · ${session.queued} queued` : ''}</span>
      );
    case 'ended':
      return (
        <span className="pill">ended{session.endedReason ? ` (${session.endedReason})` : ''}</span>
      );
  }
}

function Composer({
  value,
  onChange,
  busy,
  error,
  onSubmit,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  error: unknown;
  onSubmit: () => void;
  placeholder?: string;
}) {
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim()) onSubmit();
  };
  return (
    <form className="composer" onSubmit={submit}>
      <textarea
        className="grow"
        value={value}
        placeholder={placeholder ?? 'Message the agent'}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (value.trim()) onSubmit();
          }
        }}
        rows={2}
      />
      <div className="stack">
        <button type="submit" className="primary" disabled={busy || !value.trim()}>
          Send
        </button>
        {error ? <span className="error">{errorMessage(error)}</span> : null}
      </div>
    </form>
  );
}
